/* global axios */
import ApiClient from './ApiClient';

class SalesFunnelAPI extends ApiClient {
  constructor() {
    super('', { accountScoped: true });
  }

  // Funnels (Pipelines)
  getFunnels() {
    return axios.get(`${this.baseUrl()}/funnels`);
  }

  createFunnel(data) {
    return axios.post(`${this.baseUrl()}/funnels`, data);
  }

  updateFunnel(funnelId, data) {
    return axios.patch(`${this.baseUrl()}/funnels/${funnelId}`, data);
  }

  // Funnel Stages
  getStages(funnelId) {
    const params = funnelId ? { funnel_id: funnelId } : {};
    return axios.get(`${this.baseUrl()}/funnel_stages`, { params });
  }

  createStage(data) {
    return axios.post(`${this.baseUrl()}/funnel_stages`, data);
  }

  updateStage(stageId, data) {
    return axios.patch(`${this.baseUrl()}/funnel_stages/${stageId}`, data);
  }

  deleteStage(stageId) {
    return axios.delete(`${this.baseUrl()}/funnel_stages/${stageId}`);
  }

  reorderStages(funnelId, stages) {
    return axios.post(`${this.baseUrl()}/funnel_stages/reorder`, {
      funnel_id: funnelId,
      stages,
    });
  }

  // Deal Labels
  getDealLabels() {
    return axios.get(`${this.baseUrl()}/deal_labels`);
  }

  createDealLabel(data) {
    return axios.post(`${this.baseUrl()}/deal_labels`, data);
  }

  updateDealLabel(labelId, data) {
    return axios.put(`${this.baseUrl()}/deal_labels/${labelId}`, data);
  }

  deleteDealLabel(labelId) {
    return axios.delete(`${this.baseUrl()}/deal_labels/${labelId}`);
  }

  // Deals
  getDeals(params = {}) {
    return axios.get(`${this.baseUrl()}/deals`, { params });
  }

  getDeal(dealId) {
    return axios.get(`${this.baseUrl()}/deals/${dealId}`);
  }

  createDeal(data) {
    return axios.post(`${this.baseUrl()}/deals`, data);
  }

  updateDeal(dealId, data) {
    // Wrap under `deal` so association setters like deal_label_ids survive.
    // Rails' param wrapping only auto-nests real columns, dropping them otherwise.
    return axios.patch(`${this.baseUrl()}/deals/${dealId}`, { deal: data });
  }

  deleteDeal(dealId) {
    return axios.delete(`${this.baseUrl()}/deals/${dealId}`);
  }

  moveDeal(dealId, funnelStageId, password) {
    return axios.post(`${this.baseUrl()}/deals/${dealId}/move`, {
      funnel_stage_id: funnelStageId,
      password: password,
    });
  }

  // Deal Activities
  getDealActivities(dealId) {
    return axios.get(`${this.baseUrl()}/deals/${dealId}/deal_activities`);
  }

  // Reports
  getReports(params = {}) {
    return axios.get(`${this.baseUrl()}/deals/reports`, { params });
  }

  // Deal Tasks
  getTasks(dealId) {
    return axios.get(`${this.baseUrl()}/deals/${dealId}/deal_tasks`);
  }

  createTask(dealId, data) {
    return axios.post(`${this.baseUrl()}/deals/${dealId}/deal_tasks`, data);
  }

  updateTask(dealId, taskId, data) {
    return axios.patch(
      `${this.baseUrl()}/deals/${dealId}/deal_tasks/${taskId}`,
      data
    );
  }

  deleteTask(dealId, taskId) {
    return axios.delete(
      `${this.baseUrl()}/deals/${dealId}/deal_tasks/${taskId}`
    );
  }

  // Deal Task Comments
  getTaskComments(dealId, taskId) {
    return axios.get(
      `${this.baseUrl()}/deals/${dealId}/deal_tasks/${taskId}/deal_task_comments`
    );
  }

  createTaskComment(dealId, taskId, content) {
    return axios.post(
      `${this.baseUrl()}/deals/${dealId}/deal_tasks/${taskId}/deal_task_comments`,
      { deal_task_comment: { content } }
    );
  }

  deleteTaskComment(dealId, taskId, commentId) {
    return axios.delete(
      `${this.baseUrl()}/deals/${dealId}/deal_tasks/${taskId}/deal_task_comments/${commentId}`
    );
  }

  // Global task list (across deals) for the "My tasks" work queue
  getAllTasks(params = {}) {
    return axios.get(`${this.baseUrl()}/deal_tasks`, { params });
  }

  // Account-wide funnel activity log (audit trail)
  getActivityLog(params = {}) {
    return axios.get(`${this.baseUrl()}/deal_activity_logs`, { params });
  }

  updateTaskStatus(taskId, status) {
    return axios.patch(`${this.baseUrl()}/deal_tasks/${taskId}`, { status });
  }

  bulkUpdateDeals(payload) {
    return axios.post(`${this.baseUrl()}/deals/bulk_update`, payload);
  }

  // Deal Lost Reasons Catalog
  getLostReasons() {
    return axios.get(`${this.baseUrl()}/deal_lost_reasons`);
  }

  createLostReason(data) {
    return axios.post(`${this.baseUrl()}/deal_lost_reasons`, data);
  }

  updateLostReason(reasonId, data) {
    return axios.patch(`${this.baseUrl()}/deal_lost_reasons/${reasonId}`, data);
  }

  deleteLostReason(reasonId) {
    return axios.delete(`${this.baseUrl()}/deal_lost_reasons/${reasonId}`);
  }

  // Deal Activities Create (for Notes)
  createDealActivity(dealId, data) {
    return axios.post(
      `${this.baseUrl()}/deals/${dealId}/deal_activities`,
      data
    );
  }

  // Internet plans (Products & Services)
  updateInternetPlans(plans) {
    return axios.patch(`${this.baseUrl()}/internet_plans`, {
      internet_plans: plans,
    });
  }
}

export default new SalesFunnelAPI();
